import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { useEffect, useState, type ReactElement } from "react";
import { ContentHeader, EmptyState, Field, Modal, QueryStatus, StatusBadge, useToast } from "@cx/ui";
import type {
  BillingCancelResult,
  BillingRunResult,
  ContractBillingList,
  ContractBillingListItem,
  ContractSettings,
} from "@cx/contracts";
import { formatBRL, formatCompetence, formatDateBR, todayInSaoPaulo } from "@cx/domain";
import { can, type Permissions } from "@cx/db";
import { errorMessage } from "../../errors";
import { useSession } from "../../session";
import { TITLE_STATUS_LABEL } from "../finance/labels";
import { NFSE_STATUS_LABEL, NFSE_STATUS_TONE, competenceOfDate, competenceOptions } from "./labels";

type Confirm = { kind: "one"; billing: ContractBillingListItem } | { kind: "month" } | null;

/**
 * Serviços › Cobranças de contratos (RF-SER-02): cobranças por competência, "Gerar cobranças" (conta a receber, Pix e
 * NFS-e dos contratos devidos), "Excluir cobranças do mês" e estorno de uma cobrança (com motivo; nada é apagado) e o
 * dia da geração automática.
 */
export function ContractBillingsPage(): ReactElement {
  const { companyId, me, api } = useSession();
  const perms = me.permissions as Permissions;
  const qc = useQueryClient();
  const toast = useToast();
  const today = todayInSaoPaulo();
  const [competence, setCompetence] = useState(competenceOfDate(today));
  const [status, setStatus] = useState("active");
  const canRun = can(perms, "contracts", "create") && can(perms, "receivables", "create");
  const canCancel = can(perms, "contracts", "update") && can(perms, "receivables", "update");
  const list = useInfiniteQuery({
    queryKey: ["contract-billings", companyId, "list", competence, status],
    initialPageParam: "",
    queryFn: ({ pageParam }) =>
      api<ContractBillingList>(
        `/contract-billings?${new URLSearchParams({
          competence,
          ...(status ? { status } : {}),
          ...(pageParam ? { cursor: pageParam } : {}),
        })}`,
      ),
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });
  const rows = list.data?.pages.flatMap((p) => p.data) ?? [];
  const totals = list.data?.pages[0]?.totals;
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ["contract-billings", companyId] });
    void qc.invalidateQueries({ queryKey: ["contracts", companyId] });
  };

  const [runKey, setRunKey] = useState(() => crypto.randomUUID());
  const [result, setResult] = useState<BillingRunResult | null>(null);
  const run = useMutation({
    mutationFn: () =>
      api<BillingRunResult>("/contract-billings/run", {
        method: "POST",
        headers: { "Idempotency-Key": runKey },
        body: JSON.stringify({ competence }),
      }),
    onSuccess: (r) => {
      setRunKey(crypto.randomUUID());
      setResult(r);
      toast(
        "success",
        r.created.length === 0
          ? `Nenhuma cobrança nova em ${formatCompetence(r.competence)}`
          : `${r.created.length} ${r.created.length === 1 ? "cobrança gerada" : "cobranças geradas"} em ${formatCompetence(r.competence)}`,
      );
      refresh();
    },
    onError: (e: Error) => {
      toast("danger", errorMessage(e));
      refresh();
    },
  });

  const [confirm, setConfirm] = useState<Confirm>(null);
  const [reason, setReason] = useState("");
  const [cancelKey, setCancelKey] = useState(() => crypto.randomUUID());
  const open = (c: Confirm) => {
    setReason("");
    setCancelKey(crypto.randomUUID());
    setConfirm(c);
  };
  const cancel = useMutation({
    mutationFn: (c: NonNullable<Confirm>) =>
      c.kind === "one"
        ? api<BillingCancelResult>(`/contract-billings/${c.billing.id}/cancel`, {
            method: "POST",
            headers: { "Idempotency-Key": cancelKey },
            body: JSON.stringify({ reason }),
          })
        : api<BillingCancelResult>("/contract-billings/cancel-competence", {
            method: "POST",
            headers: { "Idempotency-Key": cancelKey },
            body: JSON.stringify({ competence, reason }),
          }),
    onSuccess: (r) => {
      setConfirm(null);
      const n = r.canceled.length;
      toast("success", n === 1 ? "Cobrança estornada" : `${n} cobranças estornadas`);
      if (r.skipped.length > 0)
        toast(
          "warning",
          `Não estornadas: ${r.skipped.map((s) => `contrato nº ${s.contractNumber} (${s.reason})`).join("; ")}`,
        );
      refresh();
    },
    onError: (e: Error) => {
      // Bloqueio do estorno (NFS-e autorizada, baixa, Pix): a mensagem vai no aviso e o diálogo fecha.
      setConfirm(null);
      toast("danger", errorMessage(e));
      refresh();
    },
  });

  return (
    <>
      <ContentHeader
        title="Cobranças de contratos"
        breadcrumb={["Serviços", "Cobranças de contratos"]}
        actions={
          <>
            <Link to="/servicos/contratos" className="btn btn-outline-secondary">
              Contratos
            </Link>
            {canCancel && (
              <button
                type="button"
                className="btn btn-outline-danger"
                disabled={!totals || totals.activeCount === 0 || cancel.isPending}
                onClick={() => open({ kind: "month" })}
              >
                Excluir cobranças do mês
              </button>
            )}
            {canRun && (
              <button
                type="button"
                className="btn btn-primary"
                disabled={run.isPending}
                onClick={() => run.mutate()}
              >
                Gerar cobranças
              </button>
            )}
          </>
        }
      />
      {result && <RunResult r={result} onClose={() => setResult(null)} />}
      <div className="box card mb-3">
        <div className="row g-2 p-3 pb-0">
          <div className="col-6 col-md-3">
            <label className="form-label" htmlFor="cb-competence">
              Competência
            </label>
            <select
              id="cb-competence"
              className="form-select"
              value={competence}
              onChange={(e) => {
                setResult(null);
                setCompetence(e.target.value);
              }}
            >
              {competenceOptions(today).map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
          <div className="col-6 col-md-3">
            <label className="form-label" htmlFor="cb-status">
              Situação
            </label>
            <select
              id="cb-status"
              className="form-select"
              value={status}
              onChange={(e) => setStatus(e.target.value)}
            >
              <option value="active">Ativas</option>
              <option value="canceled">Estornadas</option>
              <option value="">Todas</option>
            </select>
          </div>
        </div>
        <QueryStatus loading={list.isLoading} error={list.error} />
        {list.isSuccess && rows.length === 0 && (
          <EmptyState
            title={`Nenhuma cobrança em ${formatCompetence(competence)}`}
            description="“Gerar cobranças” lança a conta a receber (e o Pix e a NFS-e, se o contrato pedir) de cada contrato ativo devido na competência."
            actions={canRun ? [{ label: "Gerar cobranças", onClick: () => run.mutate() }] : []}
          />
        )}
        {rows.length > 0 && (
          <>
            <div className="table-responsive">
              <table className="table align-middle mb-0" aria-label="Cobranças de contratos">
                <thead>
                  <tr>
                    <th scope="col">Contrato</th>
                    <th scope="col">Cliente</th>
                    <th scope="col">Vencimento</th>
                    <th scope="col" className="text-end">
                      Valor
                    </th>
                    <th scope="col">Conta a receber</th>
                    <th scope="col">NFS-e</th>
                    <th scope="col">Situação</th>
                    <th scope="col">
                      <span className="visually-hidden">Ações</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((b) => (
                    <tr key={b.id}>
                      <td>
                        <Link to="/servicos/contratos/$id" params={{ id: b.contract.id }}>
                          Contrato nº {b.contract.number}
                        </Link>
                        {b.contract.description && (
                          <div className="small text-muted">{b.contract.description}</div>
                        )}
                      </td>
                      <td>{b.contact.name}</td>
                      <td>{formatDateBR(b.dueDate)}</td>
                      <td className="text-end">{formatBRL(b.amountCents)}</td>
                      <td>
                        {b.title ? (
                          can(perms, "receivables", "read") ? (
                            <Link to="/financas/contas-a-receber/$id" params={{ id: b.title.id }}>
                              {TITLE_STATUS_LABEL[b.title.status]}
                            </Link>
                          ) : (
                            TITLE_STATUS_LABEL[b.title.status]
                          )
                        ) : (
                          "—"
                        )}
                      </td>
                      <td>
                        {b.serviceInvoice ? (
                          <Link to="/servicos/notas-de-servico/$id" params={{ id: b.serviceInvoice.id }}>
                            <StatusBadge tone={NFSE_STATUS_TONE[b.serviceInvoice.status]}>
                              {b.serviceInvoice.nfseNumber ? `nº ${b.serviceInvoice.nfseNumber} · ` : ""}
                              {NFSE_STATUS_LABEL[b.serviceInvoice.status]}
                            </StatusBadge>
                          </Link>
                        ) : (
                          "—"
                        )}
                      </td>
                      <td>
                        <StatusBadge tone={b.status === "active" ? "success" : "secondary"}>
                          {b.status === "active" ? "Ativa" : "Estornada"}
                        </StatusBadge>
                        {b.cancelReason && <div className="small text-muted">{b.cancelReason}</div>}
                      </td>
                      <td className="text-end">
                        {canCancel && b.status === "active" && (
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-danger"
                            aria-label={`Estornar cobrança do contrato nº ${b.contract.number}`}
                            onClick={() => open({ kind: "one", billing: b })}
                          >
                            Estornar
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 p-3">
              {totals && (
                <span className="text-muted small" data-testid="cb-totals">
                  {totals.activeCount} {totals.activeCount === 1 ? "cobrança ativa" : "cobranças ativas"} ·
                  total {formatBRL(totals.activeCents)}
                </span>
              )}
              {list.hasNextPage && (
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm"
                  disabled={list.isFetchingNextPage}
                  onClick={() => list.fetchNextPage()}
                >
                  Carregar mais
                </button>
              )}
            </div>
          </>
        )}
      </div>
      <AutoBillingCard />
      <Modal
        open={confirm !== null}
        title={
          confirm?.kind === "one"
            ? `Estornar cobrança do contrato nº ${confirm.billing.contract.number}`
            : `Excluir cobranças de ${formatCompetence(competence)}`
        }
        onClose={() => setConfirm(null)}
        footer={
          <>
            <button type="button" className="btn btn-outline-secondary" onClick={() => setConfirm(null)}>
              Voltar
            </button>
            <button
              type="button"
              className="btn btn-danger"
              disabled={cancel.isPending || reason.trim().length < 3}
              onClick={() => confirm && cancel.mutate(confirm)}
            >
              Estornar
            </button>
          </>
        }
      >
        <p>
          {confirm?.kind === "one"
            ? "A conta a receber da cobrança é cancelada e o contrato volta a poder ser cobrado nesta competência."
            : "As cobranças ativas da competência são estornadas (as contas a receber são canceladas)."}{" "}
          Cobrança com baixa, Pix ativo ou NFS-e autorizada não é estornada. Nada é apagado: fica o registro
          do estorno.
        </p>
        <Field label="Motivo" required className="col-12">
          <input value={reason} maxLength={500} data-autofocus onChange={(e) => setReason(e.target.value)} />
        </Field>
      </Modal>
    </>
  );
}

function RunResult({ r, onClose }: { r: BillingRunResult; onClose(): void }) {
  return (
    <div className="alert alert-info alert-dismissible" role="status" data-testid="cb-run-result">
      <strong>
        {formatCompetence(r.competence)}: {r.created.length}{" "}
        {r.created.length === 1 ? "cobrança gerada" : "cobranças geradas"}
      </strong>
      {(r.nfse.queued > 0 || r.nfse.drafts > 0) && (
        <div>
          NFS-e: {r.nfse.queued} {r.nfse.queued === 1 ? "enviada" : "enviadas"}
          {r.nfse.drafts > 0 && `, ${r.nfse.drafts} em rascunho`}.
        </div>
      )}
      {r.nfse.errors.map((e) => (
        <div key={e.contractNumber}>
          NFS-e do contrato nº {e.contractNumber}: {e.message}
        </div>
      ))}
      {r.skipped.map((s) => (
        <div key={s.contractId}>
          Contrato nº {s.contractNumber} não cobrado: {s.reason}
        </div>
      ))}
      {r.hasMore && (
        <div>Ainda há contratos a cobrar nesta competência: clique em “Gerar cobranças” de novo.</div>
      )}
      <button type="button" className="btn-close" aria-label="Fechar" onClick={onClose} />
    </div>
  );
}

/** Geração automática: dia do mês (1–28) em que o sistema gera as cobranças da competência sozinho. */
function AutoBillingCard() {
  const { companyId, me, api } = useSession();
  const perms = me.permissions as Permissions;
  const qc = useQueryClient();
  const toast = useToast();
  const key = ["contracts", companyId, "settings"];
  const q = useQuery({ queryKey: key, queryFn: () => api<ContractSettings>("/contracts/settings") });
  const [day, setDay] = useState("");
  useEffect(() => {
    if (q.data) setDay(q.data.autoDay === null ? "" : String(q.data.autoDay));
  }, [q.data]);
  const canEdit = can(perms, "contracts", "update");
  const save = useMutation({
    mutationFn: () =>
      api<ContractSettings>("/contracts/settings", {
        method: "PUT",
        body: JSON.stringify({ autoDay: day ? Number(day) : null }),
      }),
    onSuccess: (s) => {
      qc.setQueryData(key, s);
      toast("success", s.autoDay === null ? "Geração automática desligada" : "Geração automática salva");
    },
    onError: (e: Error) => toast("danger", errorMessage(e)),
  });
  const s = q.data;
  return (
    <section className="box card p-3" aria-labelledby="cb-auto">
      <h2 id="cb-auto" className="h6">
        Geração automática
      </h2>
      <QueryStatus loading={q.isLoading} error={q.error} />
      {s && (
        <form
          className="row align-items-end"
          onSubmit={(e) => {
            e.preventDefault();
            if (canEdit) save.mutate();
          }}
        >
          <Field
            label="Dia do mês"
            className="col-md-4"
            hint="Gera as cobranças da competência neste dia (1 a 28), com NFS-e e Pix conforme o contrato."
          >
            <select
              className="form-select"
              value={day}
              disabled={!canEdit}
              onChange={(e) => setDay(e.target.value)}
            >
              <option value="">Desligada</option>
              {Array.from({ length: 28 }, (_, k) => k + 1).map((d) => (
                <option key={d} value={d}>
                  Dia {d}
                </option>
              ))}
            </select>
          </Field>
          <div className="col-md-5 mb-3 text-muted small" data-testid="cb-next-run">
            {s.nextRunOn ? `Próxima geração: ${formatDateBR(s.nextRunOn)}.` : "Sem geração automática."}
            {s.lastAutoCompetence && ` Última automática: ${formatCompetence(s.lastAutoCompetence)}.`}
          </div>
          {canEdit && (
            <div className="col-md-3 mb-3">
              <button type="submit" className="btn btn-outline-primary" disabled={save.isPending}>
                Salvar
              </button>
            </div>
          )}
        </form>
      )}
    </section>
  );
}
